// Customer portal — Messages section.
//
// Threaded chat with the retailer (designer / sales contact):
//   - Pre-loaded with `project.messages` from the chrome's project payload
//   - Polls `/api/customer/messages` every 3 seconds for new replies, with
//     visibility-pause (matches retailer messages page)
//   - Optimistic send: append `pending` bubble → mark `sent` on success, or
//     `failed` if the POST errors
//   - Enter submits, Shift+Enter newline
//
// PLATFORM-ONLY (2026-05-19 fix per [[project-platform-messaging-only]]):
//   - No `tel:`, `sms:`, or `mailto:` UI controls.
//   - No per-bubble channel pills (legacy showed SMS / Email / WhatsApp /
//     Phone / In-app badges; modular customer portal is in-app only).
//   - Outbound is always `channel: "in_app"`.
//
// Source-of-truth (legacy):
//   /var/www/kitchenos/customer-standalone.html  ~line 724  (#page-messages)
//   /var/www/kitchenos/customer-live.js          ~line 283  (loadMessages, sendMsg)
//
// Backend
// -------
//   GET  /api/customer/messages          — kitchenos-api/modules/customer_portal.js:737
//                                          returns { ok, threads: [{ id, supplier,
//                                          subject, messages: [{ id, from|direction,
//                                          text|body, ts|createdAt, channel? }] }] }
//   POST /api/customer/messages          — kitchenos-api/modules/customer_portal.js:753
//                                          body: { subject, body, supplierId,
//                                          attachments? }
//   POST /api/customer/messages/mark-read{ threadId } — :785
//
// Notes
// -----
// - We flatten all threads into a single, time-ordered conversation in V1.
//   The legacy portal exposes per-thread navigation, but for the modular
//   "single-card" portal the master spec (section 03_customer_driver_wizard.md)
//   asks for one chat panel with the retailer. We pick the most-recent thread
//   when sending; if there are none we let the server create one by sending
//   without `supplierId` (it defaults to the customer's bound retailer).

import { ApiError } from "@tkos/api-client";

import type {
  CustomerMessage,
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./messages.css";

// ── Constants ───────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;

// Channel label/colour maps removed 2026-05-19 — customer portal does not
// surface SMS / Email / WhatsApp / Phone badges. The CustomerMessage type
// still exposes a `channel` field for backwards-compat with the server,
// but we no longer render it on the bubble.

// ── Server shapes (defensive — these come from a legacy endpoint) ──────────

interface MessageAttachment {
  id: string;
  url: string;
  name: string;
  mimeType: string;
  size: number;
  kind: "image" | "pdf" | "other";
}

interface ServerMessage {
  id?: string;
  from?: string;
  direction?: string;
  text?: string;
  body?: string;
  ts?: string | number;
  createdAt?: string | number;
  channel?: string;
  read?: boolean;
  unread?: boolean;
  attachments?: unknown;
}

interface ServerThread {
  id?: string;
  threadId?: string;
  subject?: string;
  supplier?: string;
  supplierId?: string;
  unread?: number;
  lastTs?: string | number;
  messages?: ServerMessage[];
}

interface ListResponse {
  ok?: boolean;
  threads?: ServerThread[];
}

interface SendResponse {
  ok?: boolean;
  message?: ServerMessage;
  error?: string;
}

// ── In-memory state ────────────────────────────────────────────────────────

interface UiMessage {
  /** Stable id — server id or a `tmp-*` placeholder. */
  id: string;
  direction: "inbound" | "outbound";
  body: string;
  channel: CustomerMessage["channel"];
  /** ms-since-epoch */
  ts: number;
  attachments?: MessageAttachment[];
  pending?: boolean;
  failed?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function relTime(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  const diffSec = Math.round((ms - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 7) return RTF.format(Math.round(diffSec / 86400), "day");
  return DATE_FMT.format(new Date(ms));
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function tmpId(): string {
  return `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function coerceTs(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

function coerceChannel(raw: unknown): CustomerMessage["channel"] {
  if (typeof raw !== "string") return "in_app";
  const lc = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (lc === "email" || lc === "sms" || lc === "whatsapp" || lc === "phone" || lc === "in_app") {
    return lc;
  }
  if (lc === "inapp") return "in_app";
  return "in_app";
}

const ATTACH_ACCEPT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;
const ATTACH_MAX_BYTES = 10 * 1024 * 1024;

function attachKind(mime: string): MessageAttachment["kind"] {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  return "other";
}

function normaliseAttachments(raw: unknown): MessageAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: MessageAttachment[] = [];
  for (const a of raw) {
    if (!a || typeof a !== "object") continue;
    const r = a as Record<string, unknown>;
    const id   = typeof r.id   === "string" ? r.id   : "";
    const url  = typeof r.url  === "string" ? r.url  : "";
    const name = typeof r.name === "string" ? r.name : "";
    const mime = typeof r.mimeType === "string" ? r.mimeType : "";
    const size = typeof r.size === "number" ? r.size : 0;
    if (!id || !url) continue;
    out.push({ id, url, name: name || id, mimeType: mime, size, kind: attachKind(mime) });
  }
  return out;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function renderAttachmentsHtml(items: MessageAttachment[] | undefined): string {
  if (!items || items.length === 0) return "";
  return `<div class="msg-bubble__attachments">${items
    .map((a) => {
      if (a.kind === "image") {
        return `
          <a class="msg-attach msg-attach--image"
             href="${esc(a.url)}"
             target="_blank" rel="noopener noreferrer"
             aria-label="${esc(a.name)}">
            <img src="${esc(a.url)}" alt="${esc(a.name)}" loading="lazy" />
          </a>
        `;
      }
      const sizeLabel = formatBytes(a.size);
      return `
        <a class="msg-attach msg-attach--file"
           href="${esc(a.url)}"
           target="_blank" rel="noopener noreferrer"
           download="${esc(a.name)}">
          <span class="msg-attach__icon" aria-hidden="true">📄</span>
          <span class="msg-attach__meta">
            <span class="msg-attach__name">${esc(a.name)}</span>
            ${sizeLabel ? `<span class="msg-attach__size">${esc(sizeLabel)}</span>` : ""}
          </span>
        </a>
      `;
    })
    .join("")}</div>`;
}

interface UploadResponse {
  ok: boolean;
  attachments?: unknown;
  error?: string;
}

async function uploadAttachments(files: File[]): Promise<MessageAttachment[]> {
  if (files.length === 0) return [];
  for (const f of files) {
    if (!ATTACH_ACCEPT_TYPES.includes(f.type as (typeof ATTACH_ACCEPT_TYPES)[number])) {
      throw new Error(`${f.name}: only JPG, PNG, WebP or PDF files are allowed.`);
    }
    if (f.size > ATTACH_MAX_BYTES) {
      throw new Error(`${f.name} is over the 10 MB limit.`);
    }
  }
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  const res = await fetch("/api/message-attachments/upload", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.text()).slice(0, 200); } catch { /* ignore */ }
    throw new Error(detail || `Upload failed (HTTP ${res.status}).`);
  }
  const json = (await res.json()) as UploadResponse;
  if (!json.ok) throw new Error(json.error || "Upload failed.");
  return normaliseAttachments(json.attachments);
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

/** Project-supplied messages → UiMessage. */
function fromProject(m: CustomerMessage): UiMessage {
  return {
    id: m.id,
    direction: m.direction,
    body: m.body,
    channel: m.channel,
    ts: coerceTs(m.sentAt),
  };
}

/** Server-thread message → UiMessage (customer POV).
 *
 * Legacy server uses retailer-mental-model: `from='me'` or
 * `direction='retailer-to-supplier'` means the *customer* sent it.
 * From the customer's POV this is **outbound**.
 */
function fromServer(m: ServerMessage): UiMessage {
  const dir = String(m.direction ?? "").toLowerCase();
  const fromMe =
    m.from === "me" ||
    dir === "retailer-to-supplier" ||
    dir === "outbound" ||
    dir === "customer-to-retailer";
  return {
    id: String(m.id ?? tmpId()),
    direction: fromMe ? "outbound" : "inbound",
    body: String(m.body ?? m.text ?? "").trim(),
    channel: coerceChannel(m.channel),
    ts: coerceTs(m.createdAt ?? m.ts),
    attachments: normaliseAttachments(m.attachments),
  };
}

// ── Section ────────────────────────────────────────────────────────────────

const section: CustomerSection = {
  id: "messages",
  label: "Messages",

  shouldRender(_: CustomerProject): boolean {
    return true;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    let disposed = false;

    const retailer = ctx.project.retailer;
    const retailerName = retailer.businessName || "your designer";
    const retailerFirstWord = retailerName.split(" ")[0] ?? retailerName;
    // "Call us" button removed 2026-05-19 — platform-only per
    // [[project-platform-messaging-only]]. tel: links are banned.

    // Seed from `project.messages`. Polling will replace this list.
    let messages: UiMessage[] = ctx.project.messages.map(fromProject);
    messages.sort((a, b) => a.ts - b.ts);

    /** Server thread id we should reply into. Picked on first poll. */
    let activeThreadId: string | null = null;
    /** Supplier id the server expects when posting a reply. */
    let activeSupplierId: string | null = null;

    // Attachments the customer has picked but not yet sent.
    let pendingAttachments: MessageAttachment[] = [];

    // ── Initial chrome ───────────────────────────────────────────────────
    container.innerHTML = `
      <section class="cust-card msg-card" aria-label="Messages">
        <h2 class="cust-card-title">Chat with ${esc(retailerName)}</h2>
        <p class="msg-help">Questions about your kitchen? Send a message — we'll get back to you within a working day.</p>

        <div class="msg-body" data-role="body" aria-live="polite" aria-busy="false"></div>

        <form class="msg-foot" data-role="form" novalidate>
          <textarea class="msg-input"
                    data-role="input"
                    rows="1"
                    maxlength="8000"
                    placeholder="Message ${esc(retailerFirstWord)}…"
                    aria-label="Type a message"></textarea>
          <div class="msg-foot__actions">
            <button type="button" class="cust-btn cust-btn-secondary msg-attach-btn"
                    data-role="attach"
                    aria-label="Attach a photo or PDF">📎 Attach</button>
            <input type="file" data-role="attach-input"
                   accept="image/jpeg,image/png,image/webp,application/pdf"
                   multiple hidden />
            <button type="submit" class="cust-btn msg-send" data-role="send"
                    aria-label="Send message">Send</button>
          </div>
          <div class="msg-attach-tray" data-role="attach-tray" hidden></div>
        </form>
      </section>
    `;

    // True only when the Messages tab is the one the customer is actually
    // looking at. The shell toggles `.cust-section--active` on the section
    // wrapper; we must NOT mark messages read while sitting hidden in the
    // background (that's what kept the dashboard "New messages" card stuck at
    // 0 — a background poll cleared the unread before it could ever show).
    function isActiveTab(): boolean {
      return (
        container.closest(".cust-section")?.classList.contains("cust-section--active") ?? false
      );
    }

    const bodyEl = container.querySelector<HTMLElement>("[data-role='body']")!;
    const formEl = container.querySelector<HTMLFormElement>("[data-role='form']")!;
    const inputEl = container.querySelector<HTMLTextAreaElement>("[data-role='input']")!;
    const sendBtn = container.querySelector<HTMLButtonElement>("[data-role='send']")!;

    // Persist the unsent draft so it survives section remounts (the shell's
    // auto-refresh tears down and re-mounts every section) and page reloads.
    // UI state only — keyed by the customer's own id, per the localStorage rule.
    const draftKey = `kos_cust_msg_draft_${ctx.project.customer.id}`;
    function saveDraft(): void { try { localStorage.setItem(draftKey, inputEl.value); } catch { /* ignore */ } }
    function clearDraft(): void { try { localStorage.removeItem(draftKey); } catch { /* ignore */ } }
    const attachBtn = container.querySelector<HTMLButtonElement>("[data-role='attach']")!;
    const attachInput = container.querySelector<HTMLInputElement>("[data-role='attach-input']")!;
    const trayEl = container.querySelector<HTMLElement>("[data-role='attach-tray']")!;

    function renderTray(): void {
      if (pendingAttachments.length === 0) {
        trayEl.hidden = true;
        trayEl.innerHTML = "";
        return;
      }
      trayEl.hidden = false;
      trayEl.innerHTML = pendingAttachments
        .map((a) => {
          const preview = a.kind === "image"
            ? `<img src="${esc(a.url)}" alt="${esc(a.name)}" />`
            : `<span class="msg-attach-chip__icon" aria-hidden="true">📄</span>`;
          const sizeLabel = formatBytes(a.size);
          return `
            <div class="msg-attach-chip">
              ${preview}
              <span class="msg-attach-chip__name" title="${esc(a.name)}">${esc(a.name)}</span>
              ${sizeLabel ? `<span class="msg-attach-chip__size">${esc(sizeLabel)}</span>` : ""}
              <button type="button" class="msg-attach-chip__remove"
                      data-role="attach-remove"
                      data-attach-id="${esc(a.id)}"
                      aria-label="Remove ${esc(a.name)}">×</button>
            </div>
          `;
        })
        .join("");
    }

    // ── Render ───────────────────────────────────────────────────────────
    function render(): void {
      if (messages.length === 0) {
        bodyEl.innerHTML = `
          <div class="msg-empty">
            <div class="msg-empty__avatar" aria-hidden="true"
                 style="background: var(--brand-primary)">${esc(initialsOf(retailerName))}</div>
            <div class="msg-empty__text">
              <strong>Say hi to ${esc(retailerName)}.</strong>
              <span>No messages yet — start the conversation below.</span>
            </div>
          </div>
        `;
        return;
      }
      const html = messages
        .map((m) => {
          const side = m.direction === "outbound" ? "out" : "in";
          // Channel pill removed 2026-05-19 — customer portal is in-app
          // only; surfacing legacy SMS/Email/WhatsApp/Phone badges would
          // contradict [[project-platform-messaging-only]].
          const status = m.pending ? "Sending…" : m.failed ? "Failed — tap to retry" : relTime(m.ts);
          const cls = `msg-bubble msg-bubble--${side}${m.pending ? " is-pending" : ""}${m.failed ? " is-failed" : ""}`;
          const textHtml = m.body
            ? `<div class="msg-bubble__text">${esc(m.body).replace(/\n/g, "<br>")}</div>`
            : "";
          return `
            <article class="${cls}" data-id="${esc(m.id)}">
              ${textHtml}
              ${renderAttachmentsHtml(m.attachments)}
              <div class="msg-bubble__meta">
                <span class="msg-bubble__time">${esc(status)}</span>
              </div>
            </article>
          `;
        })
        .join("");
      const wasNearBottom =
        bodyEl.scrollHeight - bodyEl.scrollTop - bodyEl.clientHeight < 80;
      bodyEl.innerHTML = html;
      if (wasNearBottom || messages[messages.length - 1]?.direction === "outbound") {
        bodyEl.scrollTop = bodyEl.scrollHeight;
      }
    }
    render();

    // ── Backend ──────────────────────────────────────────────────────────
    async function loadMessages(): Promise<void> {
      try {
        const resp = await ctx.api<ListResponse>("/customer/messages", {
          method: "GET",
          signal: ac.signal,
        });
        if (disposed || ac.signal.aborted) return;
        if (!resp.ok || !Array.isArray(resp.threads)) return;

        // Pick the freshest thread as the "active" one to reply into.
        const threads = resp.threads.slice().sort((a, b) => {
          const at = coerceTs(a.lastTs);
          const bt = coerceTs(b.lastTs);
          return bt - at;
        });
        const top = threads[0] ?? null;
        if (top) {
          activeThreadId = String(top.id ?? top.threadId ?? "");
          activeSupplierId = top.supplierId ? String(top.supplierId) : null;
        }

        // Flatten all server messages into a single ordered list.
        const flat: UiMessage[] = [];
        for (const t of threads) {
          for (const m of t.messages ?? []) flat.push(fromServer(m));
        }
        flat.sort((a, b) => a.ts - b.ts);

        // Merge in any local pending/failed bubbles the server doesn't know
        // about yet so the user's optimistic message doesn't blink out.
        const localPending = messages.filter((m) => m.pending || m.failed);
        const serverIds = new Set(flat.map((m) => m.id));
        for (const p of localPending) if (!serverIds.has(p.id)) flat.push(p);
        flat.sort((a, b) => a.ts - b.ts);

        messages = flat;
        render();

        // Mark threads read ONLY when the customer is actually viewing the
        // Messages tab — a background poll must never pre-emptively clear the
        // unread count that drives the dashboard "New messages" card. Mark
        // EVERY unread thread (not just the freshest) so the dot fully clears
        // even when there are multiple conversations.
        if (isActiveTab()) {
          const unreadThreadIds = threads
            .filter((t) => Number(t.unread) > 0)
            .map((t) => String(t.id ?? t.threadId ?? ""))
            .filter(Boolean);
          if (unreadThreadIds.length > 0) {
            void Promise.all(
              unreadThreadIds.map((threadId) =>
                ctx
                  .api("/customer/messages/mark-read", {
                    method: "POST",
                    signal: ac.signal,
                    body: JSON.stringify({ threadId }),
                  })
                  .catch(() => {
                    /* non-critical */
                  }),
              ),
            ).then(() => {
              if (disposed || ac.signal.aborted) return;
              // Tell the dashboard + nav the unread count just dropped so the
              // "New messages" card and its dot clear immediately, instead of
              // waiting for the next 15s project refresh.
              window.dispatchEvent(new CustomEvent("tkos:cust-messages-read"));
            });
          }
        }
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        // Soft-fail — keep the existing list and try again next tick.
        console.warn("[messages] load failed", err);
      }
    }

    async function sendOne(body: string): Promise<void> {
      const trimmed = body.trim();
      const outboundAttachments = pendingAttachments.slice();
      // Allow image/PDF-only sends — at least one of body or attachments required.
      if (!trimmed && outboundAttachments.length === 0) return;

      // Optimistic insert
      const optimistic: UiMessage = {
        id: tmpId(),
        direction: "outbound",
        body: trimmed,
        channel: "in_app",
        ts: Date.now(),
        pending: true,
        attachments: outboundAttachments.length > 0 ? outboundAttachments : [],
      };
      messages.push(optimistic);
      messages.sort((a, b) => a.ts - b.ts);
      // Clear the tray now so the user sees the chip leave the input as
      // soon as they press Send.
      pendingAttachments = [];
      renderTray();
      render();

      // Disable input briefly so double-Enter doesn't double-send
      sendBtn.disabled = true;
      try {
        const payload: Record<string, unknown> = {
          subject: "Message from customer",
          body: trimmed,
        };
        if (outboundAttachments.length > 0) {
          payload["attachments"] = outboundAttachments;
          payload["attachmentIds"] = outboundAttachments.map((a) => a.id);
        }
        if (activeSupplierId) payload["supplierId"] = activeSupplierId;
        if (activeThreadId) payload["inReplyTo"] = activeThreadId;

        const resp = await ctx.api<SendResponse>("/customer/messages", {
          method: "POST",
          signal: ac.signal,
          body: JSON.stringify(payload),
        });
        if (disposed || ac.signal.aborted) return;
        const idx = messages.findIndex((m) => m.id === optimistic.id);
        if (idx >= 0) {
          const sent = resp.message;
          if (sent) {
            const live = fromServer(sent);
            // Preserve customer-POV: server may echo as "supplier-to-retailer";
            // we just sent it so it's definitely outbound.
            live.direction = "outbound";
            messages[idx] = live;
          } else {
            const cur = messages[idx];
            if (cur) {
              cur.pending = false;
            }
          }
        }
        render();
        // Pull fresh thread to pick up any server-side enrichment.
        void loadMessages();
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        const idx = messages.findIndex((m) => m.id === optimistic.id);
        if (idx >= 0) {
          const cur = messages[idx];
          if (cur) {
            cur.pending = false;
            cur.failed = true;
          }
        }
        render();
        const msg =
          err instanceof ApiError
            ? `Send failed (HTTP ${err.status})`
            : err instanceof Error
              ? err.message
              : "Send failed";
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) sendBtn.disabled = false;
      }
    }

    // ── Event wiring ─────────────────────────────────────────────────────
    function onSubmit(e: SubmitEvent): void {
      e.preventDefault();
      const text = inputEl.value;
      if (!text.trim()) return;
      inputEl.value = "";
      clearDraft();
      autoSizeInput();
      void sendOne(text);
    }
    formEl.addEventListener("submit", onSubmit);

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const text = inputEl.value;
        if (!text.trim()) return;
        inputEl.value = "";
        clearDraft();
        autoSizeInput();
        void sendOne(text);
      }
    }
    inputEl.addEventListener("keydown", onKeyDown);

    function autoSizeInput(): void {
      inputEl.style.height = "auto";
      const next = Math.min(inputEl.scrollHeight, 140);
      inputEl.style.height = `${next}px`;
    }
    inputEl.addEventListener("input", () => { autoSizeInput(); saveDraft(); });

    // Restore any persisted draft from a previous mount / reload.
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) { inputEl.value = saved; autoSizeInput(); }
    } catch { /* ignore */ }

    function onAttachClick(): void {
      attachInput.click();
    }
    attachBtn.addEventListener("click", onAttachClick);

    async function onAttachChange(): Promise<void> {
      const files = Array.from(attachInput.files ?? []);
      attachInput.value = "";
      if (files.length === 0) return;
      try {
        const uploaded = await uploadAttachments(files);
        if (disposed) return;
        pendingAttachments = [...pendingAttachments, ...uploaded];
        renderTray();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed.";
        ctx.showToast(msg, "error");
      }
    }
    attachInput.addEventListener("change", () => { void onAttachChange(); });

    function onTrayClick(e: MouseEvent): void {
      const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
        "[data-role='attach-remove']",
      );
      if (!btn) return;
      const id = btn.dataset["attachId"];
      if (!id) return;
      pendingAttachments = pendingAttachments.filter((a) => a.id !== id);
      renderTray();
    }
    trayEl.addEventListener("click", onTrayClick);

    // Retry on click of a failed bubble.
    function onBodyClick(e: MouseEvent): void {
      const bubble = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        ".msg-bubble--out.is-failed",
      );
      if (!bubble) return;
      const id = bubble.dataset.id;
      if (!id) return;
      const idx = messages.findIndex((m) => m.id === id);
      if (idx < 0) return;
      const failed = messages[idx];
      if (!failed) return;
      // Remove the failed bubble and resend.
      messages.splice(idx, 1);
      render();
      void sendOne(failed.body);
    }
    bodyEl.addEventListener("click", onBodyClick);

    // ── Polling (3s) with visibility-pause ───────────────────────────────
    let pollHandle: ReturnType<typeof setInterval> | null = null;
    function startPolling(): void {
      if (pollHandle != null) return;
      pollHandle = setInterval(() => {
        if (document.visibilityState === "hidden") return;
        void loadMessages();
      }, POLL_INTERVAL_MS);
    }
    function stopPolling(): void {
      if (pollHandle != null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
    }
    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") {
        stopPolling();
      } else {
        void loadMessages();
        startPolling();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Kick off: load fresh thread list, then start polling.
    void loadMessages();
    startPolling();

    // ── Cleanup ──────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      ac.abort();
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      formEl.removeEventListener("submit", onSubmit);
      inputEl.removeEventListener("keydown", onKeyDown);
      inputEl.removeEventListener("input", autoSizeInput);
      bodyEl.removeEventListener("click", onBodyClick);
      attachBtn.removeEventListener("click", onAttachClick);
      trayEl.removeEventListener("click", onTrayClick);
    };
  },
};

export default section;
